import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, Clock, ChevronLeft, ChevronRight, User, Play, Square, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const TimeTrackingPage = () => {
  const { user } = useAuth();
  const [timeEntries, setTimeEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(getWeekStart(new Date()));
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState('self');
  const [entryMode, setEntryMode] = useState('manual'); // 'manual' or 'timer'
  const [newEntry, setNewEntry] = useState({
    project_id: '',
    task_id: '',
    hours: '',
    start_time: '',
    end_time: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const isManager = (user?.roles || [user?.role]).some(r => ['admin', 'manager'].includes(r));

  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  function getWeekDays(weekStart) {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(day.getDate() + i);
      days.push(day);
    }
    return days;
  }

  useEffect(() => {
    fetchData();
  }, [currentWeek, selectedTeamMember]);

  const fetchData = async () => {
    try {
      const weekEnd = new Date(currentWeek);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      const params = new URLSearchParams({
        date_from: currentWeek.toISOString().split('T')[0],
        date_to: weekEnd.toISOString().split('T')[0]
      });
      
      if (selectedTeamMember && selectedTeamMember !== 'self' && isManager) {
        params.append('user_id', selectedTeamMember);
      }
      
      const [entriesRes, projectsRes, tasksRes] = await Promise.all([
        api.get(`/time-entries?${params}`),
        api.get('/projects'),
        api.get('/tasks')
      ]);
      setTimeEntries(entriesRes.data);
      setProjects(projectsRes.data);
      setTasks(tasksRes.data);
      
      if (isManager) {
        const teamRes = await api.get('/users/team');
        setTeamMembers(teamRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load time entries');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async () => {
    let hours = newEntry.hours ? parseFloat(newEntry.hours) : null;
    
    // Calculate hours from start/end time if in timer mode
    if (entryMode === 'timer' && newEntry.start_time && newEntry.end_time) {
      const start = new Date(`${newEntry.date}T${newEntry.start_time}`);
      const end = new Date(`${newEntry.date}T${newEntry.end_time}`);
      hours = (end - start) / (1000 * 60 * 60);
      
      if (hours <= 0) {
        toast.error('End time must be after start time');
        return;
      }
    }
    
    if (!hours || hours <= 0) {
      toast.error('Please enter valid hours or start/end time');
      return;
    }
    
    try {
      const payload = {
        project_id: newEntry.project_id || null,
        task_id: newEntry.task_id || null,
        hours: entryMode === 'manual' ? hours : null,
        start_time: entryMode === 'timer' ? new Date(`${newEntry.date}T${newEntry.start_time}`).toISOString() : null,
        end_time: entryMode === 'timer' ? new Date(`${newEntry.date}T${newEntry.end_time}`).toISOString() : null,
        date: new Date(newEntry.date).toISOString(),
        notes: newEntry.notes
      };
      
      await api.post('/time-entries', payload);
      
      toast.success(`Time entry added: ${hours.toFixed(1)} hours`);
      setShowAddDialog(false);
      setNewEntry({
        project_id: '',
        task_id: '',
        hours: '',
        start_time: '',
        end_time: '',
        date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      fetchData();
    } catch (err) {
      toast.error('Failed to add time entry');
    }
  };

  const prevWeek = () => {
    const prev = new Date(currentWeek);
    prev.setDate(prev.getDate() - 7);
    setCurrentWeek(prev);
  };

  const nextWeek = () => {
    const next = new Date(currentWeek);
    next.setDate(next.getDate() + 7);
    setCurrentWeek(next);
  };

  const getEntriesForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return timeEntries.filter(e => {
      const entryDate = new Date(e.date).toISOString().split('T')[0];
      return entryDate === dateStr;
    });
  };

  const getTotalHoursForDate = (date) => {
    const entries = getEntriesForDate(date);
    return entries.reduce((sum, e) => sum + (e.hours || 0), 0);
  };

  const getWeekTotal = () => {
    return timeEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
  };

  const weekDays = getWeekDays(currentWeek);
  const isToday = (date) => new Date().toDateString() === date.toDateString();

  const projectTasks = newEntry.project_id 
    ? tasks.filter(t => t.project_id === newEntry.project_id)
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="time-tracking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Time Tracking</h1>
          <p className="text-zinc-500 text-sm mt-1">Log your work hours</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchData}
            className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10" data-testid="refresh-timetracking">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
          {isManager && (
            <Select value={selectedTeamMember} onValueChange={setSelectedTeamMember}>
              <SelectTrigger className="w-48 bg-[#121212] border-white/10 text-white">
                <User className="h-4 w-4 mr-2" />
                <SelectValue placeholder="My Timesheet" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10">
                <SelectItem value="self" className="text-white hover:bg-white/10">My Timesheet</SelectItem>
                {teamMembers.map(m => (
                  <SelectItem key={m.id} value={m.id} className="text-white hover:bg-white/10">{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="add-time-entry-btn">
                <Plus className="h-4 w-4 mr-2" /> Log Time
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle>Log Time Entry</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Entry Mode Tabs */}
                <Tabs value={entryMode} onValueChange={setEntryMode} className="w-full">
                  <TabsList className="w-full bg-[#0A0A0A] border border-white/10">
                    <TabsTrigger 
                      value="manual" 
                      className="flex-1 data-[state=active]:bg-[#F5A623] data-[state=active]:text-black text-zinc-400"
                    >
                      <Clock className="h-4 w-4 mr-2" /> Manual Hours
                    </TabsTrigger>
                    <TabsTrigger 
                      value="timer" 
                      className="flex-1 data-[state=active]:bg-[#F5A623] data-[state=active]:text-black text-zinc-400"
                    >
                      <Play className="h-4 w-4 mr-2" /> Start/End Time
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Date</Label>
                  <Input
                    type="date"
                    value={newEntry.date}
                    onChange={(e) => setNewEntry({...newEntry, date: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    data-testid="time-entry-date"
                  />
                </div>
                
                {entryMode === 'manual' ? (
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">Hours</Label>
                    <Input
                      type="number"
                      step="0.25"
                      min="0.25"
                      max="24"
                      value={newEntry.hours}
                      onChange={(e) => setNewEntry({...newEntry, hours: e.target.value})}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      placeholder="e.g., 2.5"
                      data-testid="time-entry-hours"
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-400 text-xs uppercase tracking-wider">Start Time</Label>
                      <Input
                        type="time"
                        value={newEntry.start_time}
                        onChange={(e) => setNewEntry({...newEntry, start_time: e.target.value})}
                        className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                        data-testid="time-entry-start"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs uppercase tracking-wider">End Time</Label>
                      <Input
                        type="time"
                        value={newEntry.end_time}
                        onChange={(e) => setNewEntry({...newEntry, end_time: e.target.value})}
                        className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                        data-testid="time-entry-end"
                      />
                    </div>
                  </div>
                )}
                
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Project (Optional)</Label>
                  <Select 
                    value={newEntry.project_id} 
                    onValueChange={(v) => setNewEntry({...newEntry, project_id: v, task_id: ''})}
                  >
                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      <SelectItem value="" className="text-white hover:bg-white/10">No project</SelectItem>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id} className="text-white hover:bg-white/10">{p.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {newEntry.project_id && projectTasks.length > 0 && (
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">Task (Optional)</Label>
                    <Select value={newEntry.task_id} onValueChange={(v) => setNewEntry({...newEntry, task_id: v})}>
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                        <SelectValue placeholder="Select task" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        <SelectItem value="" className="text-white hover:bg-white/10">No specific task</SelectItem>
                        {projectTasks.map(t => (
                          <SelectItem key={t.id} value={t.id} className="text-white hover:bg-white/10">{t.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Notes</Label>
                  <Textarea
                    value={newEntry.notes}
                    onChange={(e) => setNewEntry({...newEntry, notes: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    placeholder="What did you work on?"
                    rows={3}
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-white/10 text-white hover:bg-white/5">
                    Cancel
                  </Button>
                  <Button onClick={handleAddEntry} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="submit-time-entry-btn">
                    Log Time
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Week Navigation */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                size="icon"
                onClick={prevWeek}
                className="h-8 w-8 border-white/10 text-white hover:bg-white/5"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <CardTitle className="text-white text-lg">
                {currentWeek.toLocaleDateString('en-IN', { month: 'long', day: 'numeric' })} - {
                  new Date(currentWeek.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('en-IN', { month: 'long', day: 'numeric', year: 'numeric' })
                }
              </CardTitle>
              <Button 
                variant="outline" 
                size="icon"
                onClick={nextWeek}
                className="h-8 w-8 border-white/10 text-white hover:bg-white/5"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="text-right">
              <p className="text-2xl font-semibold text-[#F5A623]">{getWeekTotal().toFixed(1)}h</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">This Week</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Timesheet Grid */}
          <div className="grid grid-cols-7 gap-2">
            {weekDays.map((day, index) => {
              const dayEntries = getEntriesForDate(day);
              const dayTotal = getTotalHoursForDate(day);
              const today = isToday(day);
              
              return (
                <div 
                  key={index} 
                  className={`p-3 rounded border ${today ? 'border-[#F5A623]/50 bg-[#F5A623]/5' : 'border-white/5 bg-[#0A0A0A]'}`}
                >
                  <div className="text-center mb-3">
                    <p className={`text-xs ${today ? 'text-[#F5A623]' : 'text-zinc-500'} uppercase`}>
                      {day.toLocaleDateString('en-IN', { weekday: 'short' })}
                    </p>
                    <p className={`text-lg font-semibold ${today ? 'text-[#F5A623]' : 'text-white'}`}>
                      {day.getDate()}
                    </p>
                  </div>
                  
                  <div className="space-y-2 min-h-[80px]">
                    {dayEntries.map((entry, i) => (
                      <div 
                        key={i} 
                        className="p-2 bg-[#121212] rounded text-xs border border-white/5"
                        title={entry.notes || entry.project_title || 'Time entry'}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-white font-medium">{entry.hours?.toFixed(1)}h</span>
                          {entry.start_time && entry.end_time && (
                            <span className="text-zinc-600 text-[10px]">
                              {new Date(entry.start_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        {entry.project_title && (
                          <p className="text-zinc-500 truncate mt-1">{entry.project_title}</p>
                        )}
                      </div>
                    ))}
                    
                    {dayEntries.length === 0 && (
                      <div className="flex items-center justify-center h-[80px] text-zinc-600 text-xs">
                        No entries
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-3 pt-2 border-t border-white/5 text-center">
                    <p className={`text-sm font-semibold ${dayTotal > 0 ? 'text-white' : 'text-zinc-600'}`}>
                      {dayTotal > 0 ? `${dayTotal.toFixed(1)}h` : '-'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Recent Entries List */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-lg">This Week's Entries</CardTitle>
        </CardHeader>
        <CardContent>
          {timeEntries.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              <Clock className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No time entries this week</p>
              <p className="text-xs text-zinc-600 mt-1">Click "Log Time" to add your first entry</p>
            </div>
          ) : (
            <div className="space-y-2">
              {timeEntries.map((entry, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-white/5 rounded hover:border-white/10 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-center min-w-[60px]">
                      <p className="text-lg font-semibold text-white">{entry.hours?.toFixed(1)}h</p>
                      {entry.start_time && entry.end_time && (
                        <p className="text-[10px] text-zinc-600">
                          {new Date(entry.start_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} - {new Date(entry.end_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium">
                        {entry.project_title || entry.task_title || 'General Work'}
                      </p>
                      <p className="text-xs text-zinc-500">
                        {new Date(entry.date).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short' })}
                        {entry.user_name && isManager && selectedTeamMember && ` • ${entry.user_name}`}
                        {entry.notes && ` • ${entry.notes}`}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TimeTrackingPage;
